/**
 * Audit Service
 * Handles audit logging using Supabase backend
 */
import supabaseService from './supabaseService';
import logger from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';

const AUDIT_TABLE = 'audit_logs';

class AuditService {
  async logAudit(auditData) {
    try {
      logger.info('Creating audit log entry', { operation: 'logAudit', data: auditData });

      const auditEntry = {
        id: uuidv4(),
        timestamp: new Date().toISOString(),
        userId: auditData.userId || null,
        userRole: auditData.userRole || null,
        action: auditData.action,
        entityType: auditData.entityType,
        entityId: auditData.entityId || null,
        details: auditData.details || {},
        ipAddress: auditData.ipAddress || null,
        userAgent: auditData.userAgent || null,
      };

      await supabaseService.insert(AUDIT_TABLE, auditEntry);

      logger.info('Audit log entry created successfully', {
        auditId: auditEntry.id,
        operation: 'logAudit',
      });

      return auditEntry;
    } catch (error) {
      logger.error('Error creating audit log entry', error, { operation: 'logAudit' });
      return null;
    }
  }

  async getAllAuditLogs() {
    try {
      logger.info('Fetching all audit logs', { operation: 'getAllAuditLogs' });

      const logs = await supabaseService.select(AUDIT_TABLE, {
        order: { column: 'timestamp', ascending: false },
      });

      logger.info('Audit logs fetched successfully', {
        operation: 'getAllAuditLogs',
        count: logs?.length || 0,
      });

      return logs || [];
    } catch (error) {
      logger.error('Error fetching audit logs', error, { operation: 'getAllAuditLogs' });
      return [];
    }
  }

  async getAuditLogsByUser(userId) {
    try {
      logger.info('Fetching audit logs by user', { userId, operation: 'getAuditLogsByUser' });

      const logs = await supabaseService.select(AUDIT_TABLE, {
        filters: [{ column: 'userId', value: userId }],
        order: { column: 'timestamp', ascending: false },
      });

      logger.info('User audit logs fetched', {
        userId,
        count: logs?.length || 0,
        operation: 'getAuditLogsByUser',
      });

      return logs || [];
    } catch (error) {
      logger.error('Error fetching user audit logs', error, {
        userId,
        operation: 'getAuditLogsByUser',
      });
      return [];
    }
  }

  async getAuditLogsByEntity(entityType, entityId) {
    try {
      logger.info('Fetching audit logs by entity', {
        entityType,
        entityId,
        operation: 'getAuditLogsByEntity',
      });

      const logs = await supabaseService.select(AUDIT_TABLE, {
        filters: [
          { column: 'entityType', value: entityType },
          { column: 'entityId', value: entityId },
        ],
        order: { column: 'timestamp', ascending: false },
      });

      logger.info('Entity audit logs fetched', {
        entityType,
        entityId,
        count: logs?.length || 0,
        operation: 'getAuditLogsByEntity',
      });

      return logs || [];
    } catch (error) {
      logger.error('Error fetching entity audit logs', error, {
        entityType,
        entityId,
        operation: 'getAuditLogsByEntity',
      });
      return [];
    }
  }

  async getAuditLogsByAction(action) {
    try {
      logger.info('Fetching audit logs by action', { action, operation: 'getAuditLogsByAction' });

      const logs = await supabaseService.select(AUDIT_TABLE, {
        filters: [{ column: 'action', value: action }],
        order: { column: 'timestamp', ascending: false },
      });

      logger.info('Action audit logs fetched', {
        action,
        count: logs?.length || 0,
        operation: 'getAuditLogsByAction',
      });

      return logs || [];
    } catch (error) {
      logger.error('Error fetching action audit logs', error, {
        action,
        operation: 'getAuditLogsByAction',
      });
      return [];
    }
  }

  async getAuditLogsByDateRange(startDate, endDate) {
    try {
      logger.info('Fetching audit logs by date range', {
        startDate,
        endDate,
        operation: 'getAuditLogsByDateRange',
      });

      const logs = await supabaseService.select(AUDIT_TABLE, {
        filters: [
          { column: 'timestamp', operator: 'gte', value: startDate },
          { column: 'timestamp', operator: 'lte', value: endDate },
        ],
        order: { column: 'timestamp', ascending: false },
      });

      logger.info('Date range audit logs fetched', {
        startDate,
        endDate,
        count: logs?.length || 0,
        operation: 'getAuditLogsByDateRange',
      });

      return logs || [];
    } catch (error) {
      logger.error('Error fetching date range audit logs', error, {
        operation: 'getAuditLogsByDateRange',
      });
      return [];
    }
  }
}

const auditService = new AuditService();
export default auditService;

